// SPDX-License-Identifier: AGPL-3.0-or-later

import * as Modal from '@app/features/app/components/dialogs/Modal';
import ChannelAnnouncementStore, {
	getCleanChannelTopic,
} from '@app/features/channel/announcements/ChannelAnnouncementStore';
import type {Channel} from '@app/features/channel/models/Channel';
import {Button} from '@app/features/ui/button/Button';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import * as ToastCommands from '@app/features/ui/commands/ToastCommands';
import {CheckIcon, TrashIcon} from '@phosphor-icons/react';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useCallback, useState} from 'react';

interface SetChannelAnnouncementModalProps {
	channel: Channel;
}

export const SetChannelAnnouncementModal: React.FC<SetChannelAnnouncementModalProps> = observer(
	({channel}: SetChannelAnnouncementModalProps) => {
		const current = ChannelAnnouncementStore.getAnnouncement(channel);
		const initialText = current?.text ?? getCleanChannelTopic(channel.topic) ?? '';
		const [text, setText] = useState(initialText);
		const [isSaving, setIsSaving] = useState(false);

		const handleSave = useCallback(
			async (e?: React.FormEvent) => {
				if (e) e.preventDefault();
				const trimmed = text.trim();
				setIsSaving(true);
				try {
					if (!trimmed) {
						await ChannelAnnouncementStore.deleteAnnouncement(channel.id);
						ToastCommands.success('Announcement removed');
					} else {
						await ChannelAnnouncementStore.setAnnouncement(channel.id, trimmed, true);
						ToastCommands.success('Sticky announcement saved & pinned for all members!');
					}
					ModalCommands.pop();
				} catch (error) {
					ToastCommands.error('Failed to save announcement. Please check channel permissions.');
				} finally {
					setIsSaving(false);
				}
			},
			[channel.id, text],
		);

		const handleDelete = useCallback(async () => {
			setIsSaving(true);
			try {
				await ChannelAnnouncementStore.deleteAnnouncement(channel.id);
				ToastCommands.success('Announcement unpinned');
				ModalCommands.pop();
			} catch (error) {
				ToastCommands.error('Failed to unpin announcement.');
			} finally {
				setIsSaving(false);
			}
		}, [channel.id]);

		return (
			<Modal.Root size="small" centered data-flx="channel.set-channel-announcement-modal.modal-root">
				<form onSubmit={handleSave} style={{display: 'contents'}}>
					<Modal.Header title="Sticky Channel Announcement" />
					<Modal.Content>
						<Modal.ContentLayout>
							<p style={{fontSize: '0.875rem', color: 'var(--text-primary-muted)', margin: '0 0 1rem 0'}}>
								Pin an announcement banner to the top of <strong>#{channel.name}</strong>. All members will see it when
								viewing the channel and can expand or collapse it anytime.
							</p>

							<textarea
								autoFocus
								value={text}
								disabled={isSaving}
								onChange={(e) => setText(e.target.value)}
								onKeyDown={(e) => {
									if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
										e.preventDefault();
										void handleSave();
									}
								}}
								placeholder="Enter channel announcement or guidelines... (Ctrl+Enter to save)"
								rows={5}
								style={{
									width: '100%',
									boxSizing: 'border-box',
									background: 'var(--surface-primary, rgba(0, 0, 0, 0.25))',
									border: '1px solid var(--border-subtle, rgba(255, 255, 255, 0.15))',
									borderRadius: '6px',
									padding: '0.75rem',
									color: 'var(--text-primary)',
									fontSize: '0.875rem',
									fontFamily: 'inherit',
									resize: 'vertical',
									outline: 'none',
								}}
							/>
						</Modal.ContentLayout>
					</Modal.Content>

					<Modal.Footer>
						<div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%'}}>
							{current && current.pinned ? (
								<Button
									type="button"
									variant="danger"
									disabled={isSaving}
									onClick={handleDelete}
									leftIcon={<TrashIcon size={16} />}
								>
									Unpin
								</Button>
							) : (
								<div />
							)}

							<div style={{display: 'flex', gap: '0.5rem'}}>
								<Button type="button" variant="secondary" disabled={isSaving} onClick={() => ModalCommands.pop()}>
									Cancel
								</Button>
								<Button
									type="submit"
									variant="primary"
									disabled={!text.trim() || isSaving}
									submitting={isSaving}
									leftIcon={<CheckIcon size={16} weight="bold" />}
								>
									Save Announcement
								</Button>
							</div>
						</div>
					</Modal.Footer>
				</form>
			</Modal.Root>
		);
	},
);
